namespace $.$$ {

	const Content = $bog_smalljs_content

	// In-browser embedder, same model as the build-time index. Loaded lazily
	// via $mol_import.module so the ~30MB model is only fetched on first use.
	const SEMANTIC_CDN = 'https://cdn.jsdelivr.net/npm/@xenova/transformers@2.17.2/dist/transformers.min.js'

	// One searchable section of a page: everything between two headings, plus
	// the same section of the English original. A result deep-links to `anchor`,
	// so this is also the unit the snippet is quoted from.
	type Chunk = {
		/** Heading text, verbatim — the anchor to jump to. Empty for the preamble. */
		anchor: string,
		/** Lowercased heading, localized and English, for the section-pick bonus. */
		head: string,
		head_en: string,
		/** Lowercased section source, heading line included. */
		text: string,
		text_en: string,
		/** Section source as written, for the result snippet. */
		raw: string,
	}

	// One searchable page. All haystacks are pre-lowercased: the corpus is
	// rebuilt only when the language changes, so matching stays a plain
	// substring scan on every keystroke. `*_en` holds the English original —
	// see corpus() for why it is kept alongside the translation.
	type Doc = {
		slug: string,
		title: string,
		title_en: string,
		chunks: readonly Chunk[],
	}

	export class $bog_smalljs_search extends $.$bog_smalljs_search {

		@ $mol_action
		close() { this.open( false ); return null }

		// Focus the input. Called from an action (never from a @$mol_mem —
		// focused() is a reactive setter, and writing another cell inside a
		// mem freezes this component's rendering). $mol defers the actual
		// DOM .focus() a tick, so it lands after the overlay is shown.
		@ $mol_action
		focus() {
			this.Field().focused( true )
			return null
		}

		// Navigate to a doc page, landing on the section that matched. Explicit
		// arg writes (not the link's own href) so a result ALWAYS lands on its
		// page — even when it targets the page you are already on. $mol_link
		// would otherwise treat a "current" link as a toggle and strip the args,
		// bouncing you home.
		//
		// The anchor argument is written on every jump, `null` when the match was
		// page-level, so a result can never inherit the heading of the previous
		// one and open somewhere unrelated.
		@ $mol_action
		go( slug: string ) {
			const anchor = this.result_anchor( slug )
			const key = this.anchor_key()
			const arg = this.$.$mol_state_arg
			arg.value( 'section', 'docs' )
			arg.value( 'page', slug )
			if( key ) arg.value( key, anchor || null )
			this.open( false )
			return null
		}

		@ $mol_action
		pick( slug: string, event?: Event ) {
			event?.preventDefault() // stop $mol_link's toggle navigation
			this.go( slug )
			return null
		}

		// Enter activates the highlighted result (or the first one).
		@ $mol_action
		activate( event?: unknown ) {
			const ids = this.result_ids()
			if( !ids.length ) return null
			this.go( ids[ this.active() ] )
			return null
		}

		// --- Highlighted result (keyboard ↑/↓) ---------------------------
		// Keyed by the current result set so a new query resets to the top,
		// while stays reactive so the highlight re-renders on every move.
		@ $mol_mem_key
		active_at( _key: string, next?: number ) {
			return next ?? 0
		}

		active( next?: number ) {
			return this.active_at( this.result_ids().join( '|' ), next )
		}

		@ $mol_action
		select_next( event?: KeyboardEvent ) {
			event?.preventDefault()
			const n = this.result_ids().length
			if( !n ) return null
			this.active( Math.min( n - 1, this.active() + 1 ) )
			return null
		}

		@ $mol_action
		select_prev( event?: KeyboardEvent ) {
			event?.preventDefault()
			const n = this.result_ids().length
			if( !n ) return null
			this.active( Math.max( 0, this.active() - 1 ) )
			return null
		}

		result_current( slug: string ) {
			const ids = this.result_ids()
			return ids[ this.active() ] === slug
		}

		/** Active UI language; reading it makes search reactive to switches. */
		lang() {
			return this.$.$mol_locale.lang()
		}

		// All doc pages as a flat search corpus, in the current UI language.
		// Both the translation and the English original are kept, because on a
		// non-English locale people still type English terms ("routing", "mem")
		// and those have to keep matching. On English — or on a page with no
		// translation — page_md/page_title return the very same strings, so the
		// two halves share one lowercased copy and are scored once.
		@ $mol_mem
		corpus(): readonly Doc[] {
			const lang = this.lang()
			const seen = new Set< string >()
			const docs = [] as Doc[]
			for( const section of Content.sections() ) {
				for( const group of section.groups ) {
					for( const slug of group.pages ) {
						if( seen.has( slug ) ) continue
						seen.add( slug )
						const page = Content.page( slug )
						if( !page ) continue
						const title = Content.page_title( slug, lang ) ?? page.title
						const md = Content.page_md( slug, lang ) ?? page.md
						const title_low = title.toLowerCase()
						docs.push( {
							slug,
							title: title_low,
							title_en: title === page.title ? title_low : page.title.toLowerCase(),
							chunks: this.chunks( md, page.md ),
						} )
					}
				}
			}
			return docs
		}

		/**
		 * Cuts a page into one chunk per heading, plus the preamble, and pairs
		 * each with the matching section of the English original **by position**:
		 * translations are generated from the English page and keep its heading
		 * structure, so the n-th section means the same thing in both.
		 *
		 * If the two outlines ever disagree, the page degrades to a single
		 * anchor-less chunk. Ranking is unaffected — the score of a page is the
		 * sum over its chunks either way — only the deep link is dropped.
		 */
		chunks( md: string, md_en: string ): readonly Chunk[] {

			const sections = $bog_smalljs_outline.sections( md )
			const translated = md !== md_en
			const sections_en = translated ? $bog_smalljs_outline.sections( md_en ) : sections

			if( sections_en.length !== sections.length ) {
				const text = md.toLowerCase()
				return [ { anchor: '', head: '', head_en: '', text, text_en: md_en.toLowerCase(), raw: md } ]
			}

			return sections.map( ( section, index ) => {
				const text = section.md.toLowerCase()
				const head = section.title.toLowerCase()
				// Untranslated page (or English locale): both halves are the same
				// string, and the scorer scans such a pair once instead of twice.
				const text_en = translated ? sections_en[ index ].md.toLowerCase() : text
				const head_en = translated ? sections_en[ index ].title.toLowerCase() : head
				return { anchor: section.title, head, head_en, text, text_en, raw: section.md }
			} )
		}

		// --- Full-text (instant, offline) --------------------------------
		// Raw keyword score for every page, keyed by slug, together with the
		// section of that page the query matched best. Works with zero network
		// and is what shows the moment you type.
		//
		// A page scores exactly what a whole-page scan would give it — the
		// chunks partition the markdown, and the page-title bonus is booked to
		// the preamble — so page ranking is unchanged by the split.
		@ $mol_mem
		full_text_hits(): Map< string, { score: number, chunk: Chunk | null } > {

			const hits = new Map< string, { score: number, chunk: Chunk | null } >()
			const query = this.query().trim().toLowerCase()
			if( !query ) return hits
			const terms = query.split( /\s+/ ).filter( Boolean )

			for( const doc of this.corpus() ) {

				// Untranslated page (or English locale): both halves are the same
				// string, so scan it once instead of double-counting every hit.
				const titles = doc.title === doc.title_en ? [ doc.title ] : [ doc.title, doc.title_en ]
				let title_score = 0
				for( const term of terms ) {
					for( const title of titles ) if( title.includes( term ) ) title_score += 10
				}

				let total = 0
				let best: Chunk | null = null
				let best_rank = 0

				for( let index = 0; index < doc.chunks.length; index++ ) {

					const chunk = doc.chunks[ index ]
					const texts = chunk.text === chunk.text_en ? [ chunk.text ] : [ chunk.text, chunk.text_en ]
					let score = 0
					for( const term of terms ) {
						for( const text of texts ) score += this.term_count( text, term )
					}
					// The page title belongs to the preamble, which is also the
					// chunk that means "open at the top".
					if( index === 0 ) score += title_score
					total += score

					// Picking a section is a different question from ranking pages:
					// a term in the section's own heading names the place to jump
					// to, so it weighs the same as a page title here. Kept out of
					// `total` so page order stays what it was.
					const heads = chunk.head === chunk.head_en ? [ chunk.head ] : [ chunk.head, chunk.head_en ]
					let rank = score
					for( const term of terms ) {
						for( const head of heads ) if( head.includes( term ) ) rank += 10
					}

					// Strictly greater: on a tie the earlier section wins, and the
					// preamble comes first — a page whose title is the whole match
					// opens at the top rather than at some arbitrary heading.
					if( rank > best_rank ) {
						best_rank = rank
						best = chunk.anchor ? chunk : null
					}
				}

				if( total > 0 ) hits.set( doc.slug, { score: total, chunk: best } )
			}

			return hits
		}

		full_text_scores(): Map< string, number > {
			const scores = new Map< string, number >()
			for( const [ slug, hit ] of this.full_text_hits() ) scores.set( slug, hit.score )
			return scores
		}

		/** Section of `slug` the query matched best, or null for a page-level hit. */
		result_chunk( slug: string ) {
			return this.full_text_hits().get( slug )?.chunk ?? null
		}

		/** Heading to deep-link to, empty when the page should open at the top. */
		result_anchor( slug: string ) {
			return this.result_chunk( slug )?.anchor ?? ''
		}

		/** Occurrences of `term` in already-lowercased `text`. */
		term_count( text: string, term: string ) {
			let idx = text.indexOf( term ), count = 0
			while( idx >= 0 ) { count++; idx = text.indexOf( term, idx + term.length ) }
			return count
		}

		// --- Semantic (lazy, in-browser) ---------------------------------
		// The embedder is loaded from a CDN on demand and suspends until ready.
		// Reads of these suspend; callers catch the suspense so full-text keeps
		// showing, and re-render once the model resolves. If anything fails,
		// full-text remains the result — semantics are strictly additive.

		@ $mol_mem
		extractor(): ( ( text: string, opts: unknown ) => Promise< { data: ArrayLike< number > } > ) {
			const mod = this.$.$mol_import.module( SEMANTIC_CDN ) as {
				pipeline: ( task: string, model: string ) => Promise< unknown >,
				env: { allowLocalModels: boolean, useBrowserCache: boolean },
			}
			// Fetch weights from the HF hub, not from this dev server's /models/
			// (which returns a non-JSON error page). Cache in the browser.
			mod.env.allowLocalModels = false
			mod.env.useBrowserCache = true
			return $mol_wire_sync( this ).build_pipeline( mod ) as never
		}

		build_pipeline( mod: { pipeline: ( task: string, model: string ) => Promise< unknown > } ) {
			return mod.pipeline( 'feature-extraction', $bog_smalljs_embeddings.model() )
		}

		// Embed the current query into a normalized vector (suspends while the
		// model loads / infers).
		@ $mol_mem
		query_vector(): readonly number[] {
			const query = this.query().trim()
			if( !query ) return []
			const pipe = this.extractor()
			const out = $mol_wire_sync( this ).run_embed( pipe, query )
			return Array.from( out.data )
		}

		run_embed( pipe: ( text: string, opts: unknown ) => Promise< { data: ArrayLike< number > } >, query: string ) {
			return pipe( query, { pooling: 'mean', normalize: true } )
		}

		// Cosine similarity per page. Empty until the query vector is ready.
		semantic_scores(): Map< string, number > {
			const scores = new Map< string, number >()
			let vec: readonly number[] = []
			try { vec = this.query_vector() }
			catch( error ) { if( error instanceof Promise ) return scores; throw error }
			if( !vec.length ) return scores
			for( const row of $bog_smalljs_embeddings.index() ) {
				let dot = 0
				const v = row.vector
				for( let i = 0; i < vec.length; i++ ) dot += vec[ i ] * v[ i ] // both L2-normalized → dot = cosine
				scores.set( row.slug, dot )
			}
			return scores
		}

		// 'idle' | 'loading' | 'ready' | 'error' — drives the hint.
		model_status(): string {
			if( !this.query().trim() ) return 'idle'
			try { this.query_vector(); return 'ready' }
			catch( error ) { return error instanceof Promise ? 'loading' : 'error' }
		}

		// --- Merge -------------------------------------------------------
		@ $mol_mem
		ranked(): { slug: string, score: number }[] {
			const ft = this.full_text_scores()
			const sem = this.semantic_scores()

			if( sem.size === 0 ) {
				// Full-text only (semantics not ready / unavailable).
				return [ ...ft.entries() ]
					.sort( ( a, b ) => b[ 1 ] - a[ 1 ] )
					.slice( 0, 8 )
					.map( ( [ slug, score ] ) => ( { slug, score } ) )
			}

			// Blend: normalize full-text to 0..1, add cosine. Keyword hits stay
			// strong; semantic surfaces relevant pages with no literal match.
			const ft_max = Math.max( 1, ...ft.values() )
			const slugs = new Set( [ ...ft.keys(), ...sem.keys() ] )
			const rows: { slug: string, score: number }[] = []
			for( const slug of slugs ) {
				const f = ( ft.get( slug ) ?? 0 ) / ft_max
				const s = Math.max( 0, sem.get( slug ) ?? 0 )
				const score = f * 0.6 + s * 0.9
				if( score > 0.15 ) rows.push( { slug, score } )
			}
			return rows.sort( ( a, b ) => b.score - a.score ).slice( 0, 8 )
		}

		hint_text() {
			const query = this.query().trim()
			if( !query ) return 'Type to search the documentation.'
			const n = this.ranked().length
			const status = this.model_status()
			const tail = status === 'loading' ? ' · loading semantic…' : status === 'ready' ? ' · semantic' : ''
			return n ? `${ n } result${ n > 1 ? 's' : '' } — ↑↓ to move, Enter to open${ tail }` : 'No matches.'
		}

		result_ids() {
			return this.ranked().map( row => row.slug )
		}

		result_rows() {
			return this.result_ids().map( slug => this.Result( slug ) )
		}

		// The link's own href, for middle-click and copy-link. Mirrors go():
		// the anchor key is always present so the URL never keeps the heading of
		// whatever was open before.
		result_arg( slug: string ): Record< string, string | null > {
			const arg: Record< string, string | null > = { section: 'docs', page: slug }
			const key = this.anchor_key()
			if( key ) arg[ key ] = this.result_anchor( slug ) || null
			return arg
		}

		result_title( slug: string ) {
			return Content.page_title( slug, this.lang() ) ?? slug
		}

		// Snippet is quoted from the same section the result jumps to, so what you
		// read is what you land on. Page-level hits (and semantic-only ones, which
		// have no chunk) quote the head of the page as before.
		//
		// It always comes from the page in the reader's language, matching the
		// sidebar and the page itself. When the hit was on the English original
		// only, the term is absent here and we fall back to the start of the
		// section rather than quoting a language nobody picked.
		result_snippet( slug: string ) {
			const source = this.result_chunk( slug )?.raw ?? Content.page_md( slug, this.lang() ) ?? ''
			const md = source
				.replace( /```[\s\S]*?```/g, ' ' )
				.replace( /[#`*>]/g, ' ' )
				.replace( /\s+/g, ' ' )
				.trim()
			const term = this.query().trim().toLowerCase().split( /\s+/ )[ 0 ] ?? ''
			const at = md.toLowerCase().indexOf( term )
			if( at < 0 ) return md.slice( 0, 130 ) + '…'
			const start = Math.max( 0, at - 40 )
			return ( start > 0 ? '…' : '' ) + md.slice( start, start + 150 ).trim() + '…'
		}

	}

}
